import React, { useState, useCallback, useEffect, useRef } from "react";
import { ThemeProvider } from "@mui/material/styles";
import axios from "axios";
import {
  Grid,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableBody,
  Tooltip,
  Autocomplete,
  ListItemText,
  Typography,
  Chip,
  Card,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  StyledTableCellHistory,
  StyledTableRowHistory,
  StyledTextField,
  StyledButton,
  OutlinedIconFab,
  theme,
  StyledCheckbox,
  StyledTableContainer,
} from "../StyledComponents";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import KeyboardVoiceIcon from "@mui/icons-material/KeyboardVoice";
import MicOffIcon from "@mui/icons-material/MicOff";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { toast } from "react-toastify";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import {
  durationType,
  // medicineFrequencyOptions,
} from "../initialData/initialData";
import Icon from "../Utils/Icons";
import { MedicationsTable } from "./MedicationsTable";
import { formatDurationUnitForDisplay } from "../HistoriesData/durationUtils";
import { FIELD_SET_IDS } from "../config/defaults/fieldLayoutUtils";
import useSectionFieldLayout from "../config/hooks/useSectionFieldLayout";
import {
  mapMedicineSearchOption,
  MedicineOptionLabel,
} from "../Utils/medicineSearchOption";
import { getPanelsWithObservationValues } from "../HistoriesData/initialObservationUtils";
import MethodAutocomplete from "./MethodAutocomplete";

const Medications = ({
  activeSection,
  isFocusedSection = activeSection,
  handleCrossButtonClick,
  data,
  setData,
  reasonsOptions,
  routeAutoData,
  initialObservationData,
  startRecording,
  stopRecording,
  isAiEnabled,
  fieldLayout: fieldLayoutProp,
}) => {
  const fieldLayout = useSectionFieldLayout(
    fieldLayoutProp,
    FIELD_SET_IDS.medications,
  );
  const [authToken, setAuthToken] = useState(
    sessionStorage.getItem("accessToken") !== undefined &&
      sessionStorage.getItem("accessToken") !== null
      ? sessionStorage.getItem("accessToken")
      : ""
  );
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const headers = {
    Authorization: `Bearer ${authToken}`,
  };
  const [autocompleteData, setAutoCompleteData] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [filteredFrequencyOptions, setFilteredFrequencyOptions] = useState([]);
  const [suggestApiCalled, setSuggestApiCalled] = useState(false);
  const [debounceFunctionCalled, setDebounceFunctionCalled] = useState(false);
  const [
    debounceFunctionCalledForFrequency,
    setDebounceFunctionCalledForFrequency,
  ] = useState(false);
  const { transcript, resetTranscript } = useSpeechRecognition();
  const [isFocused, setIsFocused] = useState(false);
  const [listeningOn, setListeningOn] = useState(false);
  console.log("filteredOptions", filteredOptions);
  const [newMedicine, setNewMedicine] = useState({
    id: null,
    term: "",
    branded_name: "",
    generic_name: "",
    selectedAdministration: null,
    selectedMethod: null,
    reasons: [],
    additionalInstructions: [],
    time: "",
    when: "",
    frequency: "",
    dosage: "",
    duration: "",
    medicineNote: "",
  });
  const [termSelectedOrAdded, setTermSelectedOrAdded] = useState(false);
  const [frequencySelectedOrAdded, setFrequencySelectedOrAdded] =
    useState(false);
  const [medicineName, setMedicineName] = useState("");
  const [frequencyName, setFrequencyName] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [medicines, setMedicines] = useState(data);
  const [medicineFrequencyOptions, setMedicineFrequencyOptions] = useState([]);
  const [validationErrors, setValidationErrors] = useState({
    id: null,
    term: "",
    selectedAdministration: null,
    selectedMethod: null,
    reasons: "",
    additionalInstructions: "",
    duration: "",
    medicineNote: "",
  });
  const timeOptions = [
    "5 mins",
    "10 mins",
    "15 mins",
    "20 mins",
    "30 mins",
    "45 mins",
    "1 hr",
    "Immediately",
  ];
  const whenOptions = [
    "Before Food",
    "After Food",
    "Before Breakfast",
    "After Breakfast",
    "Before Lunch",
    "Before Dinner",
    "After Dinner",
    "With Food",
    "With Tea",
    "Empty Stomach",
    "Bed Time",
    "Before Bath",
    "After Bath",
    "Before Sun",
    "After Sun",
    "SoS",
  ];
  const frequencyOptions = [
    "Daily",
    "Alternate Day",
    "Weekly",
    "Weekly Twice",
    "Weekly Thrice",
    "Fort Night",
    "Monthly",
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
    "Sun",
    "Mon, Wed",
  ];
  const dosageOptions = [
    "1 - 0 - 0",
    "0 - 0 - 1",
    "1 - 0 - 1",
    "1 - 1 - 1",
    "1 - 1 - 0",
    "0 - 1 - 0",
    "0 - 1 - 1",
    "0 - 0 - 0 - 1",
  ];
  const [drugType, setDrugType] = useState({
    branded: true,
    generic: true,
  });
  const [customFreqInput, setCustomFreqInput] = useState("");
  const [durationInputValue, setDurationInputValue] = useState("");
  const [selectedSnomedCode, setSelectedSnomedCode] = useState(null);
  const [mouseDownOnOption, setMouseDownOnOption] = useState(false);
  const [mouseDownOnFrequencyOption, setMouseDownOnFrequencyOption] =
    useState(false);

  const lastFetchedMedicineType = useRef(null); // to prevent repeated calls
  const debounceTimeout = useRef(null); // useRef to persist timeout
  const skipEffectForMedicine = useRef(false);
  const skipEffectForFrequency = useRef(false);
  const medicineChosen = useRef(false);
  const frequencyChosen = useRef(false);
  console.log("initialObservationData", initialObservationData);
  // Only surface the tests the doctor entered a value for — showing every test
  // of a panel forces needless scrolling before reaching the medicine form.
  const observedPanels = getPanelsWithObservationValues(initialObservationData);
  const fallbackFrequencies = useRef(null); // to cache fallback frequencies
  const lastFetchedFrequenciesByType = useRef({}); // Cache per type

  useEffect(() => {
    setMedicines(data);
  }, [data]);

  const getDurationParts = (durationValue) => {
    const normalized = String(durationValue || "").trim();
    if (!normalized) return { value: "", unit: "", combined: "" };
    const valueMatch = normalized.match(/\d+/);
    const unitMatch = normalized
      .toLowerCase()
      .match(/\b(minutes?|hours?|days?|weeks?|months?|years?)\b/);
    return {
      value: valueMatch ? valueMatch[0] : "",
      unit: unitMatch ? unitMatch[1] : "",
      combined: normalized,
    };
  };

  useEffect(() => {
    setAuthToken(sessionStorage.getItem("accessToken"));
  }, [sessionStorage.getItem("accessToken")]);
  const themeColour = "#21cdc0";

  const fetchMedicineFrequencies = async (medType) => {
    try {
      const response = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}get_medicine_frequencies/`,
        { medicine_type: medType },
        { headers: headers }
      );

      if (response && response.data.status == "Success") {
        return response.data.frequencies;
      } else return [];
    } catch (err) {
      console.log(err);
    }
  };
  const lookupRelationshipAPI = async (sctCode) => {
    try {
      const response = await axios.post(
        `${REACT_APP_BACKEND_ROUTE_URL}snomed/relationship_lookup/`,
        {
          concept_id: sctCode,
          relation: "has manufactured dose form",
          // relation: "has presentation strength denominator unit",
          direction: "source",
        },
        { headers: headers }
      );

      if (response && response.data.status == "Success") {
        return response.data.data[0].preferredName;
      } else return null;
    } catch (err) {
      console.log(err);
    }
  };

  //Preload generic frequency options ONCE
  // useEffect(() => {
  //   const preloadFrequencies = async () => {
  //     const response = await fetchMedicineFrequencies(""); // no payload
  //     fallbackFrequencies.current = response; // store in ref
  //   };
  //   preloadFrequencies();
  // }, []);

  useEffect(() => {
    // Dictation goes to the focused section only — several sections can be open at once.
    if (isFocusedSection) {
      let timerId;

      timerId = setTimeout(() => {
        setMedicineName(transcript);
      }, 3000);

      return () => {
        clearTimeout(timerId);
      };
    }
  }, [transcript]);

  useEffect(() => {
    if (skipEffectForMedicine.current) {
      skipEffectForMedicine.current = false; // reset after skipping
      return;
    }
    // Call the API with the debounced function when medicineName changes

    let timerOut = setTimeout(() => {
      if (medicineName.trim() !== "") {
        debouncedFilterCall(medicineName.toLowerCase());
      }
    }, 500);
    return () => clearInterval(timerOut);
  }, [medicineName]);

  useEffect(() => {
    if (skipEffectForFrequency.current) {
      skipEffectForFrequency.current = false; // reset after skipping
      return;
    }
    // Call the API with the debounced function when frequencyName changes

    let timerOut = setTimeout(() => {
      if (frequencyName.trim() !== "") {
        debouncedFilterCallForFrequency(frequencyName.toLowerCase());
      }
    }, 500);
    return () => clearInterval(timerOut);
  }, [frequencyName]);

  // const startListening = (key) => {
  //   // alert("MH SL");
  //   if (transcript) {
  //     resetTranscript();
  //   }
  //   // setActiveHistorySection("Family History");
  //   SpeechRecognition.startListening({ continuous: true, language: "en-IN" });
  //   setListeningOn(true);
  // };
  const debouncedFilterCall = async (name) => {
    console.log("Debounce function called");
    let semanticTagForMedicine =
      drugType.branded && drugType.generic
        ? "real clinical drug++clinical drug"
        : drugType.branded
          ? "real clinical drug"
          : drugType.generic
            ? "clinical drug"
            : "";
    const postBody = {
      term: name,
      state: "active",
      semantictag: semanticTagForMedicine,
      acceptability: "synonyms",
      returnlimit: "100",
      refsetid: null,
      parentid: null,
      excludeparentconcept: false,
      fullconcept: true,
    };

    try {
      if (medicineName.length > 1) {
        const response = await axios.get(
          `${REACT_APP_BACKEND_ROUTE_URL}search_medicines/?name=${name}`,
          // postBody,
          { headers: headers }
        );
        if (response) {
          console.log("Snomed Response", response);
          if (
            response.data.status === "Success" &&
            response.data.data.length > 0
          ) {
            const dataToProcess =
              response.data.data.length > 300
                ? response.data.data.slice(0, 300)
                : response.data.data;

            // const filteredData = dataToProcess.map((medicineItem) => {
            //   return {
            //     term: medicineItem.term,
            //     id: medicineItem.conceptId,
            //   };
            // });
            const filteredData = dataToProcess.map(mapMedicineSearchOption);

            console.log("Filtered Medicine Data", filteredData);
            setAutoCompleteData(filteredData);
            setFilteredOptions(filteredData);
          } else {
            try {
              if (medicineName.length > 1) {
                const response = await axios.post(
                  `${REACT_APP_BACKEND_ROUTE_URL}snomed/suggest/`,
                  postBody,
                  { headers: headers }
                );
                if (response) {
                  console.log("Suggest Response", response);
                  if (
                    response.data.status === "Success" &&
                    response.data.data.length > 0
                  ) {
                    setSuggestApiCalled(true);
                    const dataToProcess =
                      response.data.data.length > 300
                        ? response.data.data.slice(0, 300)
                        : response.data.data;
                    setAutoCompleteData(dataToProcess);
                    setFilteredOptions(dataToProcess);
                    // setSelectedFromSuggestion(true);
                  } else {
                    // setSelectedFromSuggestion(true);

                    setAutoCompleteData([]);
                    setFilteredOptions([]);
                  }
                }
              }
            } catch (err) {
              console.log(err);
              toast.error(err);
            }
          }
          setDebounceFunctionCalled(true);
        }
      }
    } catch (err) {
      console.log(err);
      toast.error(err);
    }
  };
  const debouncedFilterCallForFrequency = async (name) => {
    console.log("Debounce function called");
    let semanticTagForInvestigations = "qualifier value";
    const postBody = {
      term: name,
      state: "active",
      semantictag: semanticTagForInvestigations,
      acceptability: "synonyms",
      returnlimit: "100",
      refsetid: null,
      parentid: null,
      excludeparentconcept: false,
      fullconcept: true,
    };

    try {
      if (frequencyName.length > 1) {
        const response = await axios.post(
          `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
          postBody,
          { headers: headers }
        );
        if (response) {
          console.log("Snomed Response", response);
          if (
            response.data.status === "Success" &&
            response.data.data.length > 0
          ) {
            const dataToProcess =
              response.data.data.length > 300
                ? response.data.data.slice(0, 300)
                : response.data.data;

            const filteredData = dataToProcess.map((item) => {
              return {
                term: item.term,
                id: item.conceptId,
              };
            });
            console.log("Filtered Medicine Data", filteredData);
            setAutoCompleteData(filteredData);
            setFilteredFrequencyOptions(filteredData);
          } else {
            try {
              if (frequencyName.length > 1) {
                const response = await axios.post(
                  `${REACT_APP_BACKEND_ROUTE_URL}snomed/suggest/`,
                  postBody,
                  { headers: headers }
                );
                if (response) {
                  console.log("Suggest Response", response);
                  if (
                    response.data.status === "Success" &&
                    response.data.data.length > 0
                  ) {
                    setSuggestApiCalled(true);
                    const dataToProcess =
                      response.data.data.length > 300
                        ? response.data.data.slice(0, 300)
                        : response.data.data;
                    setAutoCompleteData(dataToProcess);
                    setFilteredFrequencyOptions(dataToProcess);
                    // setSelectedFromSuggestion(true);
                  } else {
                    // setSelectedFromSuggestion(true);

                    setAutoCompleteData([]);
                    setFilteredFrequencyOptions([]);
                  }
                }
              }
            } catch (err) {
              console.log(err);
              toast.error(err);
            }
          }
          setDebounceFunctionCalledForFrequency(true);
        }
      }
    } catch (err) {
      console.log(err);
      toast.error(err);
    }
  };

  const validateFields = () => {
    const errors = {};

    // Validate Medicine Name
    if (
      fieldLayout.isFieldVisible("term", newMedicine) &&
      fieldLayout.isFieldRequired("term", newMedicine) &&
      !newMedicine.term &&
      medicineName.length === 0
    ) {
      errors.term = fieldLayout.getValidationMessage(
        "term",
        "Medicine Name is a mandatory field.",
      );
    }

    // Validate Additional Instructions
    if (
      fieldLayout.isFieldVisible("additionalInstructions", newMedicine) &&
      fieldLayout.isFieldRequired("additionalInstructions", newMedicine) &&
      newMedicine.additionalInstructions.length === 0
    ) {
      errors.additionalInstructions = fieldLayout.getValidationMessage(
        "additionalInstructions",
        "Additional Instructions is a mandatory field.",
      );
    } else if (
      fieldLayout.isFieldVisible("additionalInstructions", newMedicine) &&
      frequencyName.trim() !== ""
    ) {
      errors.additionalInstructions =
        "Press Enter to add the instruction.";
    }

    // Validate Time
    if (
      fieldLayout.isFieldVisible("time", newMedicine) &&
      fieldLayout.isFieldRequired("time", newMedicine) &&
      !newMedicine.time
    ) {
      errors.time = fieldLayout.getValidationMessage(
        "time",
        "Time is a mandatory field.",
      );
    }

    // Validate When
    if (
      fieldLayout.isFieldVisible("when", newMedicine) &&
      fieldLayout.isFieldRequired("when", newMedicine) &&
      !newMedicine.when
    ) {
      errors.when = fieldLayout.getValidationMessage(
        "when",
        "When is a mandatory field.",
      );
    }

    // Validate Frequency
    if (
      fieldLayout.isFieldVisible("frequency", newMedicine) &&
      fieldLayout.isFieldRequired("frequency", newMedicine) &&
      !newMedicine.frequency
    ) {
      errors.frequency = fieldLayout.getValidationMessage(
        "frequency",
        "Frequency is a mandatory field.",
      );
    }

    // Validate Dosage
    if (
      fieldLayout.isFieldVisible("dosage", newMedicine) &&
      fieldLayout.isFieldRequired("dosage", newMedicine) &&
      !newMedicine.dosage
    ) {
      errors.dosage = fieldLayout.getValidationMessage(
        "dosage",
        "Dosage is a mandatory field.",
      );
    }

    // Validate Duration
    const durNum = Number(getDurationParts(newMedicine?.duration).value);

    if (
      fieldLayout.isFieldVisible("duration", newMedicine) &&
      fieldLayout.isFieldRequired("duration", newMedicine) &&
      !newMedicine.duration
    ) {
      errors.duration = fieldLayout.getValidationMessage(
        "duration",
        "Duration is a mandatory field.",
      );
    } else if (
      fieldLayout.isFieldVisible("duration", newMedicine) &&
      durationInputValue &&
      !newMedicine.duration
    ) {
      errors.duration = "Please select a duration option.";
    } else if (
      fieldLayout.isFieldVisible("duration", newMedicine) &&
      newMedicine?.duration !== "" &&
      newMedicine?.duration != null &&
      durNum < 0
    ) {
      errors.duration = "Duration must not be a negative number.";
    }

    // Validate Administration
    if (
      fieldLayout.isFieldVisible("selectedAdministration", newMedicine) &&
      fieldLayout.isFieldRequired("selectedAdministration", newMedicine) &&
      !newMedicine.selectedAdministration
    ) {
      errors.selectedAdministration = fieldLayout.getValidationMessage(
        "selectedAdministration",
        "Administration is a mandatory field.",
      );
    }

    // Validate Reasons
    if (
      fieldLayout.isFieldVisible("reasons", newMedicine) &&
      fieldLayout.isFieldRequired("reasons", newMedicine) &&
      (!newMedicine.reasons || newMedicine.reasons.length === 0)
    ) {
      errors.reasons = fieldLayout.getValidationMessage(
        "reasons",
        "Reasons is a mandatory field.",
      );
    }

    // Validate Medicine Note
    if (
      fieldLayout.isFieldVisible("medicineNote", newMedicine) &&
      fieldLayout.isFieldRequired("medicineNote", newMedicine) &&
      !newMedicine.medicineNote?.trim()
    ) {
      errors.medicineNote = fieldLayout.getValidationMessage(
        "medicineNote",
        "Notes is a mandatory field.",
      );
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleAdd = async () => {
    if (!validateFields()) {
      return; // Exit if validation fails
    }

    let newDetails,
      updatedFound = {},
      updatedSelectedOptiondetsils = {},
      foundData = false;

    if (!medicineChosen.current) {
      const postBody = {
        term: medicineName,
        state: "active",
        semantictag: "real clinical drug++clinical drug",
        acceptability: "synonyms",
        returnlimit: "100",
        refsetid: null,
        parentid: null,
        excludeparentconcept: false,
        fullconcept: true,
      };

      try {
        if (medicineName.length > 1) {
          const response = await axios.get(
            `${REACT_APP_BACKEND_ROUTE_URL}search_medicines/?name=${medicineName}`,
            // postBody,
            { headers: headers }
          );
          if (response) {
            console.log("Snomed Response", response);
            if (
              response.data.status === "Success" &&
              response.data.data.length > 0
            ) {
              foundData = response.data.data.find(
                (d) => d.term === medicineName
              );
              console.log("Found Data", foundData);
              if (foundData) {
                updatedFound = {
                  ...foundData,
                  found: true,
                  id: foundData.conceptId,
                };
              } else {
                updatedSelectedOptiondetsils = {
                  hierarchy: null,
                  isPreferredTerm: null,
                  conceptState: null,
                  conceptFsn: medicineName,
                  definitionStatus: null,
                  conceptId: null,
                  languageCode: "en",
                  typeId: null,
                  term: medicineName,
                  caseSignificanceId: "CASE_INSENSITIVE",
                  id: null,
                  effectiveTime: null,
                  activeStatus: null,
                  moduleId: null,
                };
              }
            } else {
              updatedSelectedOptiondetsils = {
                hierarchy: null,
                isPreferredTerm: null,
                conceptState: null,
                conceptFsn: medicineName,
                definitionStatus: null,
                conceptId: null,
                languageCode: "en",
                typeId: null,
                term: medicineName,
                caseSignificanceId: "CASE_INSENSITIVE",
                id: null,
                effectiveTime: null,
                activeStatus: null,
                moduleId: null,
              };
            }

            if (foundData) {
              console.log("updatedFound", updatedFound);
              newDetails = {
                ...newMedicine,
                term: updatedFound.term,
                id: updatedFound.id,
                branded_name: updatedFound.branded_name || "",
                generic_name: updatedFound.generic_name || "",
                source: updatedFound.source || "snomed", // Use source from API response
              };
            } else {
              console.log(
                "updatedSelectedOptiondetsils",
                updatedSelectedOptiondetsils
              );
              newDetails = {
                ...newMedicine,
                term: updatedSelectedOptiondetsils.term,
                id: updatedSelectedOptiondetsils.id,
                branded_name: "",
                generic_name: "",
                source: "custom", // Not found in API, set as custom
              };
            }
          } else {
            newDetails = {
              ...newMedicine,
              term: medicineName,
              id: null,
              branded_name: "",
              generic_name: "",
              source: "custom", // No response or empty response, set as custom
            };
            // handleInputChange("condition", updatedSelectedOptiondetsils);
          }
        } else {
          // medicineName length <= 2
          newDetails = {
            ...newMedicine,
            term: medicineName,
            id: null,
            branded_name: "",
            generic_name: "",
            source: "custom", // Too short to search, set as custom
          };
        }
      } catch (err) {
        console.log(err);
        // toast.error(err);
        newDetails = {
          ...newMedicine,
          term: medicineName,
          id: null,
          branded_name: "",
          generic_name: "",
          source: "custom", // Error occurred, set as custom
        };
      }
    } else {
      // medicineChosen.current is true - medicine was selected from list
      // source should already be set in handleInputChange
      newDetails = { ...newMedicine };
      // Ensure source is set if it wasn't set before
      if (!newDetails.source) {
        newDetails.source = "snomed";
      }
    }

    console.log("New Diagnosis Added", newDetails);

    // Add the new term to the complaints list
    let updatedDetails = [...medicines, newDetails];
    console.log("Diagnosis Updated Details", updatedDetails);
    setMedicines(updatedDetails);
    setNewMedicine({
      id: null,
      term: "",
      branded_name: "",
      generic_name: "",
      selectedAdministration: null,
      selectedMethod: null,
      reasons: [],
      additionalInstructions: [],
      time: "",
      when: "",
      frequency: "",
      dosage: "",
      duration: "",
      medicineNote: "",
    });
    setDurationInputValue("");
    setValidationErrors({}); // Clear errors after successful submission
    setDebounceFunctionCalled(false);
    setDebounceFunctionCalledForFrequency(false);
    setTermSelectedOrAdded(false);
    setFrequencySelectedOrAdded(false);
    setMedicineName("");
    setFrequencyName("");
    setData(updatedDetails);
    medicineChosen.current = false;
    setFilteredOptions([]);
    frequencyChosen.current = false;
    setFilteredFrequencyOptions([]);
    setCustomFreqInput("");
  };

  // Function to handle input changes
  // const handleInputChange = (field, value) => {
  //   console.log("Term and its value", field, value);
  //   let newDetails;
  //   if (field == "term") {
  //     setAutoCompleteData([]);
  //     setFilteredOptions([]);
  //     setTermSelectedOrAdded(true);
  //     newDetails = {
  //       ...newMedicine,
  //       [field]: value.term,
  //       id: value.id,
  //     };
  //   } else {
  //     newDetails = {
  //       ...newMedicine,
  //       [field]: value,
  //     };
  //   }

  //   setNewMedicine(newDetails);
  // };
  const handleInputChange = (field, value) => {
    let newDetails;

    if (field === "term") {
      setAutoCompleteData([]);
      setFilteredOptions([]);
      setTermSelectedOrAdded(true);
      setSelectedSnomedCode(value.id);
      newDetails = {
        ...newMedicine,
        [field]: value.term,
        id: value.id,
        branded_name: value.branded_name || "",
        generic_name: value.generic_name || "",
        source: value.source || "snomed", // Include source from selected option
      };
    } else if (field === "additionalInstructions") {
      setFilteredFrequencyOptions([]);
      setAutoCompleteData([]);
      setFrequencySelectedOrAdded(true);
      // newDetails = {
      //   ...newMedicine,
      //   [field]: value, // already sanitized in Autocomplete's onChange
      // };
      const alreadyExists = newMedicine.additionalInstructions.some(
        (item) => item.term.toLowerCase() === value.term.toLowerCase()
      );

      if (!alreadyExists) {
        newDetails = {
          ...newMedicine,
          additionalInstructions: [
            ...newMedicine.additionalInstructions,
            { term: value.term, id: value.id },
          ],
        };
      } else {
        newDetails = { ...newMedicine };
      }
    } else {
      newDetails = {
        ...newMedicine,
        [field]: value,
      };
    }

    setNewMedicine(newDetails);
  };

  // Function to handle row edit
  const handleEditRow = (index) => {
    setMedicineName(medicines[index].term);
    const row = medicines[index];
    const durationText = getDurationParts(row.duration).combined;
    setDurationInputValue(
      getDurationParts(durationText).value
    );
    setNewMedicine({ ...row, term: "", duration: durationText });
    setEditingIndex(index);
    // handleDeleteRow(index); // Remove the row from the list temporarily
  };
  console.log("Editing Index", editingIndex);
  const handleUpdate = async () => {
    if (!validateFields()) {
      return; // Exit if validation fails
    }

    let newDetails,
      updatedFound = {},
      updatedSelectedOptiondetsils = {},
      foundData = false;

    if (!medicineChosen.current) {
      const postBody = {
        term: medicineName,
        state: "active",
        semantictag: "real clinical drug++clinical drug",
        acceptability: "synonyms",
        returnlimit: "100",
        refsetid: null,
        parentid: null,
        excludeparentconcept: false,
        fullconcept: true,
      };

      try {
        if (medicineName.length > 1) {
          const response = await axios.get(
            `${REACT_APP_BACKEND_ROUTE_URL}search_medicines/?name=${medicineName}`,
            // postBody,
            { headers: headers }
          );
          if (response) {
            console.log("Snomed Response", response);
            if (
              response.data.status === "Success" &&
              response.data.data.length > 0
            ) {
              foundData = response.data.data.find(
                (d) => d.term === medicineName
              );
              console.log("Found Data", foundData);
              if (foundData) {
                updatedFound = {
                  ...foundData,
                  found: true,
                  id: foundData.conceptId,
                };
              } else {
                updatedSelectedOptiondetsils = {
                  hierarchy: null,
                  isPreferredTerm: null,
                  conceptState: null,
                  conceptFsn: medicineName,
                  definitionStatus: null,
                  conceptId: null,
                  languageCode: "en",
                  typeId: null,
                  term: medicineName,
                  caseSignificanceId: "CASE_INSENSITIVE",
                  id: null,
                  effectiveTime: null,
                  activeStatus: null,
                  moduleId: null,
                };
              }
            } else {
              updatedSelectedOptiondetsils = {
                hierarchy: null,
                isPreferredTerm: null,
                conceptState: null,
                conceptFsn: medicineName,
                definitionStatus: null,
                conceptId: null,
                languageCode: "en",
                typeId: null,
                term: medicineName,
                caseSignificanceId: "CASE_INSENSITIVE",
                id: null,
                effectiveTime: null,
                activeStatus: null,
                moduleId: null,
              };
            }

            if (foundData) {
              console.log("updatedFound", updatedFound);
              newDetails = {
                ...newMedicine,
                term: updatedFound.term,
                id: updatedFound.id,
                branded_name: updatedFound.branded_name || "",
                generic_name: updatedFound.generic_name || "",
                source: updatedFound.source || "snomed", // Use source from API response
              };
            } else {
              console.log(
                "updatedSelectedOptiondetsils",
                updatedSelectedOptiondetsils
              );
              newDetails = {
                ...newMedicine,
                term: updatedSelectedOptiondetsils.term,
                id: updatedSelectedOptiondetsils.id,
                branded_name: "",
                generic_name: "",
                source: "custom", // Not found in API, set as custom
              };
            }
          } else {
            newDetails = {
              ...newMedicine,
              term: medicineName,
              id: null,
              branded_name: "",
              generic_name: "",
              source: "custom", // No response or empty response, set as custom
            };
            // handleInputChange("condition", updatedSelectedOptiondetsils);
          }
        } else {
          // medicineName length <= 2
          newDetails = {
            ...newMedicine,
            term: medicineName,
            id: null,
            branded_name: "",
            generic_name: "",
            source: "custom", // Too short to search, set as custom
          };
        }
      } catch (err) {
        console.log(err);
        // toast.error(err);
        newDetails = {
          ...newMedicine,
          term: medicineName,
          id: null,
          branded_name: "",
          generic_name: "",
          source: "custom", // Error occurred, set as custom
        };
      }
    } else {
      // medicineChosen.current is true - medicine was selected from list
      // source should already be set in handleInputChange
      newDetails = { ...newMedicine };
      // Ensure source is set if it wasn't set before
      if (!newDetails.source) {
        newDetails.source = "snomed";
      }
    }

    let updatedDetails = medicines.map((data, i) =>
      i == editingIndex ? newDetails : data
    );
    setMedicines(updatedDetails);
    setEditingIndex(null);
    setNewMedicine({
      id: null,
      term: "",
      branded_name: "",
      generic_name: "",
      selectedAdministration: null,
      selectedMethod: null,
      reasons: [],
      additionalInstructions: [],
      time: "",
      when: "",
      frequency: "",
      dosage: "",
      duration: "",
      medicineNote: "",
    });
    setDurationInputValue("");
    setValidationErrors({}); // Clear errors after successful submission
    setDebounceFunctionCalled(false);
    setDebounceFunctionCalledForFrequency(false);
    setTermSelectedOrAdded(false);
    setFrequencySelectedOrAdded(false);
    setMedicineName("");
    setFrequencyName("");
    setData(updatedDetails);
    medicineChosen.current = false;
    setFilteredOptions([]);
    frequencyChosen.current = false;
    setFilteredFrequencyOptions([]);
    setCustomFreqInput("");
  };
  // Function to handle row delete
  const handleDeleteRow = (index) => {
    let updatedDetails = medicines.filter((_, i) => i !== index);
    setMedicines(updatedDetails);
    setData(updatedDetails);
  };
  const memoizedHandleCrossButtonClick = useCallback(
    (sectionName, suggestionName, type) => {
      handleCrossButtonClick(sectionName, suggestionName, type);
    },
    [handleCrossButtonClick]
  );
  const handleClearName = () => {
    setValidationErrors({});
    setMedicineName("");
    resetTranscript();
    setNewMedicine({
      ...newMedicine,
      term: "",
      branded_name: "",
      generic_name: "",
    });
    setDurationInputValue("");
    setFilteredOptions([]);
    setAutoCompleteData([]);
    setDebounceFunctionCalled(false);
    setTermSelectedOrAdded(false);
    // setMedicineList([])
    setSelectedSnomedCode(null);
    lastFetchedMedicineType.current = null;
    setMedicineFrequencyOptions([]);
  };
  const handleClearFrequency = () => {
    setValidationErrors({});
    setFrequencyName("");
    resetTranscript();
    setNewMedicine({
      ...newMedicine,
      additionalInstructions: [], // Use computed property name to set the field dynamically
    });
    setFilteredFrequencyOptions([]);
    setAutoCompleteData([]);
    setDebounceFunctionCalledForFrequency(false);
    setFrequencySelectedOrAdded(false);
    // setMedicineList([])
  };
  // const handleAddOption = async (term) => {
  //   if (term.trim().length == 0) {
  //     const errors = {};
  //     errors.term = "You can't add an empty string";
  //     setValidationErrors(errors);
  //     return;
  //   }
  //   const postBody = {
  //     term: term,
  //     state: "active",
  //     semantictag: "real clinical drug++clinical drug",
  //     acceptability: "synonyms",
  //     returnlimit: "100",
  //     refsetid: null,
  //     parentid: null,
  //     excludeparentconcept: false,
  //     fullconcept: true,
  //   };

  //   try {
  //     if (medicineName.length > 1) {
  //       const response = await axios.post(
  //         `${REACT_APP_BACKEND_ROUTE_URL}snomed/search/`,
  //         postBody,
  //         { headers: headers }
  //       );
  //       if (response) {
  //         let updatedFound = {},
  //           updatedSelectedOptiondetsils = {},
  //           foundData = false;
  //         console.log("Snomed Response", response);
  //         if (
  //           response.data.status === "Success" &&
  //           response.data.data.length > 0
  //         ) {
  //           foundData = response.data.data.find((d) => d.term === term);
  //           console.log("Found Data", foundData);
  //           if (foundData) {
  //             updatedFound = {
  //               ...foundData,
  //               found: true,
  //               id: foundData.conceptId,
  //             };
  //           } else {
  //             updatedSelectedOptiondetsils = {
  //               hierarchy: null,
  //               isPreferredTerm: null,
  //               conceptState: null,
  //               conceptFsn: term,
  //               definitionStatus: null,
  //               conceptId: null,
  //               languageCode: "en",
  //               typeId: null,
  //               term: term,
  //               caseSignificanceId: "CASE_INSENSITIVE",
  //               id: null,
  //               effectiveTime: null,
  //               activeStatus: null,
  //               moduleId: null,
  //             };
  //           }
  //         } else {
  //           updatedSelectedOptiondetsils = {
  //             hierarchy: null,
  //             isPreferredTerm: null,
  //             conceptState: null,
  //             conceptFsn: term,
  //             definitionStatus: null,
  //             conceptId: null,
  //             languageCode: "en",
  //             typeId: null,
  //             term: term,
  //             caseSignificanceId: "CASE_INSENSITIVE",
  //             id: null,
  //             effectiveTime: null,
  //             activeStatus: null,
  //             moduleId: null,
  //           };
  //         }

  //         setAutoCompleteData([]);
  //         setFilteredOptions([]);
  //         setSuggestApiCalled(false);
  //         setDebounceFunctionCalled(true);
  //         if (foundData) {
  //           handleInputChange("term", updatedFound);
  //         } else {
  //           handleInputChange("term", updatedSelectedOptiondetsils);
  //         }
  //       }
  //     }
  //   } catch (err) {
  //     console.log(err);
  //     toast.error(err);
  //   }
  // };
  const handleDrugType = (type) => {
    setDrugType((prevDrugType) => ({
      ...prevDrugType,
      [type]: !prevDrugType[type],
    }));
  };

  const handleConditionTyped = (value) => {
    setMedicineName(value);

    if (value.trim() === "") {
      // Medicine name was cleared (via backspace, delete, or otherwise)
      setSelectedSnomedCode(null);
      lastFetchedMedicineType.current = null;
      setMedicineFrequencyOptions([]);
    }

    // if (debounceTimeout.current) {
    //   clearTimeout(debounceTimeout.current); // Clear previous timeout
    // }

    // debounceTimeout.current = setTimeout(() => {
    //   handleAddOption(value); // Only triggers after user stops typing
    // }, 500); // Adjust delay as needed
  };

  console.log("Medicines data", medicines);
  console.log("Reasons", reasonsOptions);
  console.log("debounceFunctionCalled", debounceFunctionCalled);
  return (
    <ThemeProvider theme={theme}>
      {/* {activeSection && (
        <Grid item xs={12}>
          <Suggestions
            sectionName={sectionName}
            suggestions={suggestions}
            activeSection={activeSection}
            aiSuggestionToggle={aiSuggestionToggle}
            ruleSuggestionToggle={ruleSuggestionToggle}
            handleCrossButtonClick={memoizedHandleCrossButtonClick}
            handleSuggestionClick={setMedicineName}
          />
        </Grid>
      )} */}
      {activeSection && (
        <>
          {observedPanels.length > 0 && (
              <Card
                sx={{
                  borderRadius: "10px",
                  boxShadow: "0 4px 4px 0 #C7C7CC inset",
                  backgroundColor: "#FFF",
                }}
              >
                <div
                  style={{
                    justifyContent: "center",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Grid item xs={12} sm={11}>
                    <Accordion
                      defaultExpanded
                      sx={{
                        mt: 2,
                        border: "1px solid #21cdc0",
                        borderRadius: 1,
                        "&.Mui-expanded:first-of-type": {
                          mt: 1,
                        },
                        "&.Mui-expanded:last-of-type": {
                          mb: 2,
                        },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1">
                          Initial Observation
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer
                          component={Paper}
                          sx={{ border: "1px solid #ccc", mb: 2 }}
                        >
                          <Table>
                            <TableHead>
                              <StyledTableRowHistory>
                                <StyledTableCellHistory
                                  bgColor={themeColour}
                                  sx={{ width: "18%" }}
                                >
                                  Panel
                                </StyledTableCellHistory>
                                <StyledTableCellHistory
                                  bgColor={themeColour}
                                  sx={{ width: "42%" }}
                                >
                                  Test Name
                                </StyledTableCellHistory>
                                <StyledTableCellHistory
                                  bgColor={themeColour}
                                  sx={{ width: "12%" }}
                                >
                                  Value
                                </StyledTableCellHistory>
                                <StyledTableCellHistory
                                  bgColor={themeColour}
                                  sx={{ width: "13%" }}
                                >
                                  Unit
                                </StyledTableCellHistory>
                                <StyledTableCellHistory
                                  bgColor={themeColour}
                                  sx={{ width: "15%" }}
                                >
                                  LOINC
                                </StyledTableCellHistory>
                              </StyledTableRowHistory>
                            </TableHead>
                            <TableBody>
                              {observedPanels.map((panel, panelIndex) =>
                                panel.tests.map((test, testIndex) => (
                                  <StyledTableRowHistory
                                    key={`${panelIndex}_${testIndex}`}
                                  >
                                    <StyledTableCellHistory>
                                      {testIndex === 0 ? panel.panel_name : ""}
                                    </StyledTableCellHistory>
                                    <StyledTableCellHistory>
                                      {test.test}
                                    </StyledTableCellHistory>
                                    <StyledTableCellHistory>
                                      {test.value || "-"}
                                    </StyledTableCellHistory>
                                    <StyledTableCellHistory>
                                      {test.unit || "-"}
                                    </StyledTableCellHistory>
                                    <StyledTableCellHistory>
                                      {test.code || "-"}
                                    </StyledTableCellHistory>
                                  </StyledTableRowHistory>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                </div>
              </Card>
            )}
          <Card
            sx={{
              borderRadius: "10px",
              boxShadow: "0 4px 4px 0 #C7C7CC inset",
              backgroundColor: "#FFF",
              backgroundImage: "url(/newImages/medicationsBackground.svg)",
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <Grid item xs={12} sm={12} md={12} sx={{ paddingTop: 0 }}>
              <Grid
                container
                spacing={2}
                sx={{
                  marginTop: 0,
                  padding: "10px",
                  paddingTop: 0,
                }}
              >
                <Grid
                  container
                  item
                  xs={12}
                  justifyContent={{ xs: "center", sm: "flex-end" }}
                  sx={{ display: "flex", flexDirection: "row", paddingTop: 0 }}
                >
                  {/* <MenuItem
                    key="generic"
                    onClick={() => handleDrugType("generic")}
                  >
                    <StyledCheckbox checked={drugType.generic} />
                    <ListItemText primary="Generic" />
                  </MenuItem>
                  <MenuItem
                    key="branded"
                    onClick={() => handleDrugType("branded")}
                  >
                    <StyledCheckbox checked={drugType.branded} />
                    <ListItemText primary="Branded" />
                  </MenuItem> */}
                </Grid>
                {fieldLayout.isFieldVisible("term", newMedicine) && (
                <Grid item {...fieldLayout.getFieldGrid("term", { xs: 12, sm: 7, md: 6 })} sx={{ order: -10 }}>
                  <StyledTextField
                    value={
                      // newMedicine.term !== "" ? newMedicine.term :
                      medicineName
                    }
                    onChange={(e) => {
                      // if (!newMedicine.term) {
                      //   setMedicineName(e.target.value);
                      // }
                      handleConditionTyped(e.target.value);
                    }}
                    onBlur={() => {
                      setTimeout(() => {
                        const shouldClear = !mouseDownOnOption;

                        if (shouldClear) {
                          // setIsFocused(false);
                          setFilteredOptions([]);
                          setDebounceFunctionCalled(false);
                          setTermSelectedOrAdded(false);
                        }

                        setMouseDownOnOption(false);
                      }, 100);
                    }}
                    onFocus={() => {
                      // setIsFocused(true);
                      if (medicineName.trim() !== "") {
                        debouncedFilterCall(medicineName.toLowerCase()); // re-trigger filtering
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <div style={{ marginRight: "5px" }}>
                            <Tooltip title="Clear">
                              <OutlinedIconFab
                                size="small"
                                variant="contained"
                                onClick={handleClearName}
                              >
                                <Icon name="clearTextField" size={32} />
                              </OutlinedIconFab>
                            </Tooltip>
                          </div>
                          {/* {!termSelectedOrAdded && (
                        <Tooltip title="Add" style={{ marginRight: "5px" }}>
                          <OutlinedIconFab
                            size="small"
                            variant="contained"
                            onClick={handleAddOption}
                          >
                            <AddIcon />
                          </OutlinedIconFab>
                        </Tooltip>
                      )} */}
                          <>
                            {isAiEnabled &&
                              fieldLayout.isCapabilityEnabled("term", "voice") &&
                              (listeningOn === false ? (
                                <Tooltip title="Start Listening">
                                  <OutlinedIconFab
                                    // onClick={startListening}
                                    onClick={() => {
                                      startRecording();
                                      setListeningOn(true);
                                    }}
                                    variant="contained"
                                    // color="defaultColor"
                                    size="small"
                                  >
                                    <Icon name="micNotInUse" size={32} />
                                  </OutlinedIconFab>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Stop Listening">
                                  <OutlinedIconFab
                                    onClick={() => {
                                      // SpeechRecognition.stopListening();
                                      stopRecording("Medications");
                                      setListeningOn(false);
                                    }}
                                    variant="outlined"
                                    color="defaultColor"
                                    size="small"
                                  >
                                    <Icon name="micInUse" size={32} />
                                  </OutlinedIconFab>
                                </Tooltip>
                              ))}
                          </>
                        </div>
                      ),
                    }}
                    label={fieldLayout.getFieldLabel("term", "Medicine Name", newMedicine)}
                    fullWidth
                    error={!!validationErrors.term} // Highlight field in red if there's an error
                    helperText={validationErrors.term} // Show error message
                  />
                  {filteredOptions.length > 0 ? (
                    <div
                      style={{
                        backgroundColor: "#f5f5f5",
                        borderRadius: "10px",
                        padding: "20px",
                        maxHeight: "200px", // Set your desired maximum height here
                        overflowY: "auto", // Add vertical scrolling
                      }}
                    >
                      {/* Display filtered medicines */}
                      {suggestApiCalled && (
                        <div style={{ color: "red" }}>
                          No Exact Matches found ! You can search for the
                          following Suggestions.
                        </div>
                      )}

                      <>
                        {filteredOptions.map((op, optionIndex) => (
                          <div key={op.id || `${op.term}-${optionIndex}`}>
                            {
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {op.added ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <StyledButton
                                      color="success"
                                      sx={{
                                        textAlign: "left",
                                        justifyContent: "flex-start",
                                        width: "100%",
                                      }}
                                    >
                                      <MedicineOptionLabel option={op} />
                                    </StyledButton>
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        flexDirection: "row",
                                        color: "green",
                                        fontStyle: "bold",
                                      }}
                                    >
                                      <span>&#10003;</span>
                                      <span>(Added)</span>
                                    </div>
                                  </div>
                                ) : (
                                  <StyledButton
                                    color="grey"
                                    sx={{
                                      color: "#000",
                                      textAlign: "left",
                                      justifyContent: "flex-start",
                                      width: "100%",
                                    }}
                                    onClick={() => {
                                      skipEffectForMedicine.current = true; // prevent debounce call
                                      medicineChosen.current = true;
                                      setMedicineName(op.term);
                                      handleInputChange("term", op);
                                      setDebounceFunctionCalled(false);
                                    }}
                                    onMouseDown={() =>
                                      setMouseDownOnOption(true)
                                    }
                                  >
                                    <MedicineOptionLabel option={op} />
                                  </StyledButton>
                                )}
                              </div>
                            }
                          </div>
                        ))}
                      </>
                    </div>
                  ) : filteredOptions.length == 0 &&
                    debounceFunctionCalled &&
                    !termSelectedOrAdded ? (
                    <div
                      style={{
                        backgroundColor: "#f5f5f5",
                        borderRadius: "10px",
                        padding: "20px",
                        maxHeight: "300px", // Set your desired maximum height here
                        overflowY: "auto", // Add vertical scrolling
                      }}
                    >
                      <div style={{ color: "red" }}>No Options Found!</div>
                    </div>
                  ) : null}
                </Grid>
                )}
                {fieldLayout.isFieldVisible("additionalInstructions", newMedicine) && (
                <Grid item {...fieldLayout.getFieldGrid("additionalInstructions", { xs: 12, sm: 6, md: 6 })} sx={{ order: -4 }}>
                  <StyledTextField
                    label={fieldLayout.getFieldLabel(
                      "additionalInstructions",
                      "Additional Instructions",
                      newMedicine,
                    )}
                    placeholder="Select or type custom direction of use"
                    fullWidth
                    error={!!validationErrors.additionalInstructions}
                    helperText={validationErrors.additionalInstructions}
                    value={frequencyName}
                    onChange={(e) => {
                      setFrequencyName(e.target.value);
                      setCustomFreqInput(e.target.value);

                      // Clear validation when input becomes empty
                      if (e.target.value.trim() === "") {
                        setValidationErrors((prev) => ({
                          ...prev,
                          additionalInstructions: "",
                        }));
                      }
                    }}
                    onBlur={() => {
                      setIsFocused(false);
                      setTimeout(() => {
                        const shouldClear = !mouseDownOnFrequencyOption;

                        if (shouldClear) {
                          // setIsFocused(false);
                          setFilteredFrequencyOptions([]);
                          setDebounceFunctionCalledForFrequency(false);
                          setTermSelectedOrAdded(false);
                        }

                        setMouseDownOnFrequencyOption(false);
                      }, 100);
                    }}
                    onFocus={() => {
                      setIsFocused(true);
                      if (frequencyName.trim() !== "") {
                        debouncedFilterCallForFrequency(
                          frequencyName.toLowerCase()
                        ); // re-trigger filtering
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && frequencyName.trim() !== "") {
                        e.preventDefault();

                        const inputTerm = frequencyName.trim();

                        const alreadyExists =
                          newMedicine.additionalInstructions.some(
                            (f) =>
                              f.term.toLowerCase() === inputTerm.toLowerCase()
                          );

                        if (!alreadyExists) {
                          setNewMedicine((prev) => ({
                            ...prev,
                            additionalInstructions: [
                              ...prev.additionalInstructions,
                              { term: inputTerm, id: null },
                            ],
                          }));
                        }

                        // Clear input
                        setFrequencyName("");

                        // Clear validation error
                        setValidationErrors((prev) => ({
                          ...prev,
                          additionalInstructions: "",
                        }));
                      }
                    }}
                    // onFocus={() => setIsFocused(true)}
                    // onBlur={() => setIsFocused(false)}
                    InputLabelProps={{
                      shrink:
                        isFocused ||
                        frequencyName !== "" ||
                        newMedicine.additionalInstructions.length > 0,
                    }}
                    InputProps={{
                      startAdornment: (() => {
                        const limit = 3;
                        const visibleChips =
                          newMedicine.additionalInstructions.slice(
                            0,
                            limit
                          );
                        const hiddenCount =
                          newMedicine.additionalInstructions.length - limit;

                        return (
                          <>
                            {visibleChips.map((freq, index) => (
                              <Chip
                                key={index}
                                label={freq.term}
                                onDelete={() => {
                                  const updated = [
                                    ...newMedicine.additionalInstructions,
                                  ];
                                  updated.splice(index, 1);
                                  setNewMedicine((prev) => ({
                                    ...prev,
                                    additionalInstructions: updated,
                                  }));
                                }}
                                style={{ margin: "2px" }}
                              />
                            ))}
                            {hiddenCount > 0 && (
                              <Chip
                                label={`+${hiddenCount}`}
                                style={{
                                  margin: "2px",
                                  backgroundColor: "#e0e0e0",
                                }}
                              />
                            )}
                          </>
                        );
                      })(),
                      endAdornment: (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                          }}
                        >
                          <div style={{ marginRight: "5px" }}>
                            <Tooltip title="Clear">
                              <OutlinedIconFab
                                size="small"
                                variant="contained"
                                onClick={handleClearFrequency}
                              >
                                <Icon name="clearTextField" size={32} />
                              </OutlinedIconFab>
                            </Tooltip>
                          </div>

                          <>
                            {/* {listeningOn === false ? (
                            <Tooltip title="Start Listening">
                              <OutlinedIconFab
                                onClick={startListening}
                                variant="contained"
                                color="defaultColor"
                                size="small"
                              >
                                <MicOffIcon />
                              </OutlinedIconFab>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Stop Listening">
                              <OutlinedIconFab
                                onClick={() => {
                                  SpeechRecognition.stopListening();
                                  setListeningOn(false);
                                }}
                                variant="outlined"
                                color="defaultColor"
                                size="small"
                              >
                                <KeyboardVoiceIcon />
                              </OutlinedIconFab>
                            </Tooltip>
                          )} */}
                          </>
                        </div>
                      ),
                    }}
                  />

                  {filteredFrequencyOptions.length > 0 ? (
                    <div
                      style={{
                        backgroundColor: "#f5f5f5",
                        borderRadius: "10px",
                        padding: "20px",
                        maxHeight: "200px", // Set your desired maximum height here
                        overflowY: "auto", // Add vertical scrolling
                      }}
                    >
                      {/* Display filtered medicines */}
                      {suggestApiCalled && (
                        <div style={{ color: "red" }}>
                          No Exact Matches found ! You can search for the
                          following Suggestions.
                        </div>
                      )}

                      <>
                        {filteredFrequencyOptions.map((op) => (
                          <div key={op}>
                            {
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {newMedicine.additionalInstructions.some(
                                  (f) => f.term === op.term
                                ) ? (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <StyledButton
                                      color="success"
                                      sx={{
                                        textAlign: "left",
                                        justifyContent: "flex-start",
                                        width: "100%",
                                      }}
                                    >
                                      {op.term}
                                    </StyledButton>
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        flexDirection: "row",
                                        color: "green",
                                        fontStyle: "bold",
                                      }}
                                    >
                                      <span>&#10003;</span>
                                      <span>(Added)</span>
                                    </div>
                                  </div>
                                ) : (
                                  <StyledButton
                                    color="grey"
                                    sx={{
                                      color: "#000",
                                      textAlign: "left",
                                      justifyContent: "flex-start",
                                      width: "100%",
                                    }}
                                    onClick={() => {
                                      skipEffectForFrequency.current = true; // prevent debounce call
                                      frequencyChosen.current = true;
                                      setFrequencyName("");
                                      handleInputChange(
                                        "additionalInstructions",
                                        op
                                      );
                                      setCustomFreqInput("");
                                      setDebounceFunctionCalledForFrequency(false);
                                    }}
                                    onMouseDown={() =>
                                      setMouseDownOnFrequencyOption(true)
                                    }
                                  >
                                    {op.term}
                                  </StyledButton>
                                )}
                              </div>
                            }
                          </div>
                        ))}
                      </>
                    </div>
                  ) : filteredFrequencyOptions.length == 0 &&
                    debounceFunctionCalledForFrequency &&
                    !frequencySelectedOrAdded ? (
                    <div
                      style={{
                        backgroundColor: "#f5f5f5",
                        borderRadius: "10px",
                        padding: "20px",
                        maxHeight: "300px", // Set your desired maximum height here
                        overflowY: "auto", // Add vertical scrolling
                      }}
                    >
                      <div style={{ color: "red" }}>No Options Found!</div>
                    </div>
                  ) : null}
                </Grid>
                )}
                {fieldLayout.isFieldVisible("time", newMedicine) && (
                <Grid item {...fieldLayout.getFieldGrid("time", { xs: 6, sm: 3, md: 2 })} sx={{ order: -9 }}>
                  <Autocomplete
                    freeSolo
                    options={timeOptions}
                    value={newMedicine.time || ""}
                    onChange={(event, newValue) =>
                      handleInputChange("time", newValue || "")
                    }
                    onInputChange={(event, newInputValue) =>
                      handleInputChange("time", newInputValue)
                    }
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        label={fieldLayout.getFieldLabel("time", "Time", newMedicine)}
                        placeholder="Select or type time"
                        fullWidth
                        error={!!validationErrors.time}
                        helperText={validationErrors.time}
                      />
                    )}
                  />
                </Grid>
                )}
                {fieldLayout.isFieldVisible("when", newMedicine) && (
                <Grid item {...fieldLayout.getFieldGrid("when", { xs: 6, sm: 3, md: 2 })} sx={{ order: -8 }}>
                  <Autocomplete
                    freeSolo
                    options={whenOptions}
                    value={newMedicine.when || ""}
                    onChange={(event, newValue) =>
                      handleInputChange("when", newValue || "")
                    }
                    onInputChange={(event, newInputValue) =>
                      handleInputChange("when", newInputValue)
                    }
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        label={fieldLayout.getFieldLabel("when", "When", newMedicine)}
                        placeholder="Select or type when"
                        fullWidth
                        error={!!validationErrors.when}
                        helperText={validationErrors.when}
                      />
                    )}
                  />
                </Grid>
                )}
                {fieldLayout.isFieldVisible("frequency", newMedicine) && (
                <Grid item {...fieldLayout.getFieldGrid("frequency", { xs: 6, sm: 3, md: 2 })} sx={{ order: -7 }}>
                  <Autocomplete
                    freeSolo
                    options={frequencyOptions}
                    value={newMedicine.frequency || ""}
                    onChange={(event, newValue) =>
                      handleInputChange("frequency", newValue || "")
                    }
                    onInputChange={(event, newInputValue) =>
                      handleInputChange("frequency", newInputValue)
                    }
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        label={fieldLayout.getFieldLabel(
                          "frequency",
                          "Frequency",
                          newMedicine,
                        )}
                        placeholder="Select or type frequency"
                        fullWidth
                        error={!!validationErrors.frequency}
                        helperText={validationErrors.frequency}
                      />
                    )}
                  />
                </Grid>
                )}
                {fieldLayout.isFieldVisible("dosage", newMedicine) && (
                <Grid item {...fieldLayout.getFieldGrid("dosage", { xs: 6, sm: 3, md: 3 })} sx={{ order: -6 }}>
                  <Autocomplete
                    freeSolo
                    options={dosageOptions}
                    value={newMedicine.dosage || ""}
                    onChange={(event, newValue) =>
                      handleInputChange("dosage", newValue || "")
                    }
                    onInputChange={(event, newInputValue) =>
                      handleInputChange("dosage", newInputValue)
                    }
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        label={fieldLayout.getFieldLabel("dosage", "Dosage", newMedicine)}
                        placeholder="Select or type dosage"
                        fullWidth
                        error={!!validationErrors.dosage}
                        helperText={validationErrors.dosage}
                      />
                    )}
                  />
                </Grid>
                )}
                {/* <Grid item xs={6} sm={5} md={6}>
                <Autocomplete
                  multiple
                  freeSolo
                  limitTags={3}
                  disableCloseOnSelect
                  clearOnBlur={false}
                  options={medicineFrequencyOptions}
                  value={newMedicine.frequency || []}
                  onFocus={async () => {
                    let snomedCodeToUse = selectedSnomedCode;

                    // CASE 1: No SNOMED selected, check for exact match
                    if (
                      !snomedCodeToUse &&
                      medicineName &&
                      filteredOptions.length > 0
                    ) {
                      const match = filteredOptions.find(
                        (opt) =>
                          opt.term.toLowerCase() === medicineName.toLowerCase()
                      );
                      console.log("match", match);
                      if (match && match.id) {
                        snomedCodeToUse = match.id;
                        setSelectedSnomedCode(match.id);
                      }
                    }

                    // CASE 2: We now either have SNOMED or not
                    if (snomedCodeToUse) {
                      const medicineType = await lookupRelationshipAPI(
                        snomedCodeToUse
                      );
                      console.log("medicineType", medicineType);
                      console.log(
                        "lastFetchedMedicineType.current",
                        lastFetchedMedicineType.current
                      );
                      // Prevent repeated API calls for same medicineType
                      if (
                        medicineType &&
                        medicineType !== lastFetchedMedicineType.current
                      ) {
                        lastFetchedMedicineType.current = medicineType;

                        //  use caching for medicineType frequencies
                        if (
                          lastFetchedFrequenciesByType.current[medicineType]
                        ) {
                          setMedicineFrequencyOptions(
                            lastFetchedFrequenciesByType.current[medicineType]
                          );
                        } else {
                          const freqs = await fetchMedicineFrequencies(
                            medicineType
                          );

                          if (freqs.length > 0) {
                            setMedicineFrequencyOptions(freqs);
                            lastFetchedFrequenciesByType.current[medicineType] =
                              freqs; // cache it
                          } else {
                            setMedicineFrequencyOptions(
                              fallbackFrequencies.current
                            );
                          }
                        }
                      } else if (medicineType) {
                        // Already fetched this medicineType

                        setMedicineFrequencyOptions(
                          lastFetchedFrequenciesByType.current[medicineType] ||
                            fallbackFrequencies.current ||
                            []
                        );
                      } else {
                        // No medicine type found, fallback

                        setMedicineFrequencyOptions(
                          fallbackFrequencies.current || []
                        );
                      }
                    } else if (
                      !snomedCodeToUse &&
                      medicineName.length === 0 &&
                      filteredOptions.length === 0
                    ) {
                      // no medicine name added
                      console.log("no snomed, no medicine name");
                      setMedicineFrequencyOptions([]);
                    } else {
                      // No SNOMED at all, fallback

                      setMedicineFrequencyOptions(
                        fallbackFrequencies.current || []
                      );
                    }
                  }}
                  onChange={(event, newValue) => {
                    const unique = [];
                    const seen = new Set();

                    for (let item of newValue) {
                      // If item is string (free text), convert to object with defaults
                      let obj =
                        typeof item === "string"
                          ? {
                              medicine_type: "Custom", // or "" or "Free Text"
                              frequency_code: item,
                              full_form: item,
                              description: item,
                            }
                          : item;

                      // Use frequency_code in lowercase as unique key
                      const key = obj.frequency_code.toLowerCase();

                      if (!seen.has(key)) {
                        seen.add(key);
                        unique.push(obj);
                      }
                    }

                    handleInputChange("frequency", unique);
                  }}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") return option;
                    return `${option.frequency_code} ${
                      option.medicine_type !== "Custom"
                        ? `(${option.full_form})`
                        : ""
                    }`;
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (
                      typeof option === "string" ||
                      typeof value === "string"
                    ) {
                      return option === value;
                    }
                    return option.frequency_code === value.frequency_code;
                  }}
                  filterOptions={(options, state) => {
                    const filtered = options.filter((opt) =>
                      `${opt.frequency_code} (${opt.full_form})`
                        .toLowerCase()
                        .includes(state.inputValue.toLowerCase())
                    );

                    // Add typed value as an option
                    if (
                      state.inputValue !== "" &&
                      !filtered.some((opt) => {
                        const label =
                          `${opt.frequency_code} (${opt.full_form})`.toLowerCase();
                        return label === state.inputValue.toLowerCase();
                      })
                    ) {
                      filtered.push(state.inputValue);
                      setCustomFreqInput(state.inputValue);
                    }

                    return filtered;
                  }}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      label="Frequency"
                      placeholder="Select or type custom frequency"
                      fullWidth
                      error={!!validationErrors.frequency}
                      helperText={validationErrors.frequency}
                    />
                  )}
                />
              </Grid> */}
                {/* <Grid item xs={6} sm={2.5} md={3}>
                <Autocomplete
                  freeSolo
                  options={medicineInstructionOptions}
                  value={newMedicine.instructions}
                  onChange={(event, newValue) =>
                    handleInputChange("instructions", newValue)
                  } // Use newValue
                  onInputChange={(event, newInputValue) => {
                    // Handle free text input separately
                    handleInputChange("instructions", newInputValue);
                  }}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      label="Instruction"
                      placeholder="Select or type custom instruction"
                      fullWidth
                      error={!!validationErrors.instructions} // Highlight field in red if there's an error
                      helperText={validationErrors.instructions} // Show error message
                    />
                  )}
                />
              </Grid> */}
                {fieldLayout.isFieldVisible("duration", newMedicine) && (
                <Grid item {...fieldLayout.getFieldGrid("duration", { xs: 12, sm: 6, md: 3 })} sx={{ order: -5 }}>
                  <Autocomplete
                    freeSolo={false}
                    options={
                      durationInputValue
                        ? durationType.map(
                          (unit) =>
                            `${durationInputValue} ${formatDurationUnitForDisplay(unit.value)}`
                        )
                        : []
                    }
                    value={
                      newMedicine.duration || null
                    }
                    inputValue={
                      newMedicine.duration || durationInputValue
                    }
                    open={
                      /^\d+$/.test(durationInputValue) &&
                      !newMedicine.duration &&
                      durationInputValue.length > 0
                    }
                    onChange={(_, selectedValue) => {
                      if (!selectedValue) return;
                      const parts = selectedValue.split(" ");
                      const numericPart = parts[0] || "";
                      setDurationInputValue(numericPart);
                      setNewMedicine((prev) => ({
                        ...prev,
                        duration: selectedValue,
                      }));
                      setValidationErrors((prev) => ({ ...prev, duration: "" }));
                    }}
                    onInputChange={(_, value, reason) => {
                      if (reason === "reset") return;
                      if (value === "") {
                        setDurationInputValue("");
                        setNewMedicine((prev) => ({
                          ...prev,
                          duration: "",
                        }));
                        return;
                      }
                      const numericPrefixMatch = value.match(/^\d+/);
                      if (numericPrefixMatch) {
                        const numericPart = numericPrefixMatch[0];
                        setDurationInputValue(numericPart);
                        setNewMedicine((prev) => ({
                          ...prev,
                          duration: "",
                        }));
                        return;
                      }
                      if (/^\d+$/.test(value)) {
                        setDurationInputValue(value);
                        setNewMedicine((prev) => ({
                          ...prev,
                          duration: "",
                        }));
                      }
                    }}
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        label={fieldLayout.getFieldLabel(
                          "duration",
                          "Duration",
                          newMedicine,
                        )}
                        fullWidth
                        error={!!validationErrors.duration}
                        helperText={validationErrors.duration}
                      />
                    )}
                  />
                </Grid>
                )}
                {fieldLayout.isFieldVisible("selectedAdministration", newMedicine) && (
                <Grid item {...fieldLayout.getFieldGrid("selectedAdministration", { xs: 5, sm: 6, md: 2 })}>
                  <Autocomplete
                    options={routeAutoData} // Predefined options for selection
                    value={newMedicine.selectedAdministration} // Bind to the selected value
                    onChange={
                      (event, newValue) =>
                        handleInputChange("selectedAdministration", newValue) // Update state on selection change
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value?.id
                    } // Define equality check
                    slotProps={{
                      paper: {
                        sx: {
                          "& .MuiAutocomplete-listbox": {
                            fontSize: "12px", // sets option font size
                          },
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        label={fieldLayout.getFieldLabel(
                          "selectedAdministration",
                          "Administration",
                          newMedicine,
                        )}
                        placeholder="Select administration method"
                        fullWidth
                        error={!!validationErrors.selectedAdministration}
                        helperText={validationErrors.selectedAdministration}
                      />
                    )}
                  />
                </Grid>
                )}
                {fieldLayout.isFieldVisible("selectedMethod", newMedicine) && (
                <Grid item {...fieldLayout.getFieldGrid("selectedMethod", { xs: 5, sm: 6, md: 2 })}>
                  <MethodAutocomplete
                    value={newMedicine.selectedMethod}
                    onChange={(newValue) =>
                      handleInputChange("selectedMethod", newValue)
                    }
                    label={fieldLayout.getFieldLabel(
                      "selectedMethod",
                      "Method",
                      newMedicine,
                    )}
                    placeholder="Search method"
                    error={!!validationErrors.selectedMethod}
                    helperText={validationErrors.selectedMethod}
                  />
                </Grid>
                )}
                {fieldLayout.isFieldVisible("reasons", newMedicine) && (
                <Grid item {...fieldLayout.getFieldGrid("reasons", { xs: 12, sm: 5, md: 3 })}>
                  <Autocomplete
                    multiple
                    limitTags={1}
                    options={reasonsOptions} // Predefined list of options
                    value={newMedicine.reasons} // Bind selected values
                    noOptionsText="No Chief Complaints Added"
                    onChange={(event, newValue) => {
                      handleInputChange("reasons", newValue); // Store only the 'condition' values
                    }}
                    slotProps={{
                      paper: {
                        sx: {
                          "& .MuiAutocomplete-listbox": {
                            fontSize: "12px", //sets option font size
                          },
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <StyledTextField
                        {...params}
                        label={fieldLayout.getFieldLabel("reasons", "Reasons", newMedicine)}
                        fullWidth
                        error={!!validationErrors.reasons}
                        helperText={validationErrors.reasons}
                      />
                    )}
                  />
                </Grid>
                )}

                {fieldLayout.isFieldVisible("medicineNote", newMedicine) && (
                <Grid item {...fieldLayout.getFieldGrid("medicineNote", { xs: 8, sm: 5, md: 3 })}>
                  <StyledTextField
                    fullWidth
                    label={fieldLayout.getFieldLabel("medicineNote", "Notes", newMedicine)}
                    value={newMedicine.medicineNote}
                    onChange={(e) =>
                      handleInputChange("medicineNote", e.target.value)
                    }
                    error={!!validationErrors.medicineNote}
                    helperText={validationErrors.medicineNote}
                  />
                </Grid>
                )}
                <Grid
                  item
                  xs={2}
                  md={1}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <StyledButton
                    onClick={editingIndex != null ? handleUpdate : handleAdd}
                    variant="contained"
                    sx={{
                      fontSize: { xs: "0.8rem", sm: "1rem" },
                      width: "100%",
                    }}
                    bgColor="#0E3F66"
                  >
                    {editingIndex != null ? "Update" : "Add"}
                  </StyledButton>
                </Grid>
              </Grid>
            </Grid>
          </Card>
        </>
      )}
      {medicines && (
        <MedicationsTable
          data={medicines}
          activeSection={activeSection}
          handleEditRow={handleEditRow}
          handleDeleteRow={handleDeleteRow}
          fieldLayout={fieldLayout}
        />
      )}
    </ThemeProvider>
  );
};

export default Medications;
